/**
 * Custom hook for managing service history operations
 */

import React from 'react';
import { customerService } from '../../../../services/customer.service';
import type { Truck } from '../types';

export const useServiceHistory = (trucks: Truck[], setTrucks: React.Dispatch<React.SetStateAction<Truck[]>>) => {
  const [expandedTruckId, setExpandedTruckId] = React.useState<string | null>(null);
  const [serviceHistoryLoading, setServiceHistoryLoading] = React.useState<{ [key: string]: boolean }>({});

  /**
   * Toggle expand/collapse for a truck's service history
   */
  const handleToggleExpand = async (truckId: string) => {
    // If clicking the same truck, collapse it
    if (expandedTruckId === truckId) {
      setExpandedTruckId(null);
      return;
    }

    // Expand the clicked truck
    setExpandedTruckId(truckId);

    // Load service history if not already loaded
    const truck = trucks.find(t => t.id === truckId);
    if (truck && !truck.serviceHistory) {
      setServiceHistoryLoading(prev => ({ ...prev, [truckId]: true }));
      try {
        const history = await customerService.getServiceHistory(truckId);
        setTrucks(prevTrucks =>
          prevTrucks.map(t =>
            t.id === truckId ? { ...t, serviceHistory: history } : t
          )
        );
      } catch (error) {
        console.error('Failed to load service history:', error);
        alert(`Failed to load service history: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setServiceHistoryLoading(prev => ({ ...prev, [truckId]: false }));
      }
    }
  };

  /**
   * Add a new service history entry
   */
  const handleAddServiceEntry = async (truckId: string) => {
    try {
      const newEntry = await customerService.addServiceHistory(truckId, {
        serviceDate: new Date().toISOString(),
        status: 'pass',
      });

      setTrucks(prevTrucks =>
        prevTrucks.map(truck =>
          truck.id === truckId
            ? { ...truck, serviceHistory: [newEntry, ...(truck.serviceHistory || [])] }
            : truck
        )
      );
    } catch (error) {
      console.error('Failed to add service entry:', error);
      alert(`Failed to add service entry: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Update service history entry status (pass/fail toggle)
   */
  const handleUpdateServiceStatus = async (truckId: string, entryId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'pass' ? 'fail' : 'pass';

    try {
      await customerService.updateServiceHistory(truckId, entryId, { status: newStatus });

      setTrucks(prevTrucks =>
        prevTrucks.map(truck =>
          truck.id === truckId
            ? {
                ...truck,
                serviceHistory: truck.serviceHistory?.map(entry =>
                  entry.id === entryId ? { ...entry, status: newStatus } : entry
                )
              }
            : truck
        )
      );
    } catch (error) {
      console.error('Failed to update service status:', error);
      alert(`Failed to update service status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Update service history entry date
   */
  const handleUpdateServiceDate = async (truckId: string, entryId: string, newDate: string) => {
    try {
      await customerService.updateServiceHistory(truckId, entryId, { serviceDate: new Date(newDate).toISOString() });

      setTrucks(prevTrucks =>
        prevTrucks.map(truck =>
          truck.id === truckId
            ? {
                ...truck,
                serviceHistory: truck.serviceHistory?.map(entry =>
                  entry.id === entryId ? { ...entry, serviceDate: newDate } : entry
                )
              }
            : truck
        )
      );
    } catch (error) {
      console.error('Failed to update service date:', error);
      alert(`Failed to update service date: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Delete service history entry
   */
  const handleDeleteServiceEntry = async (truckId: string, entryId: string) => {
    try {
      await customerService.deleteServiceHistory(truckId, entryId);

      setTrucks(prevTrucks =>
        prevTrucks.map(truck =>
          truck.id === truckId
            ? {
                ...truck,
                serviceHistory: truck.serviceHistory?.filter(entry => entry.id !== entryId)
              }
            : truck
        )
      );
    } catch (error) {
      console.error('Failed to delete service entry:', error);
      alert(`Failed to delete service entry: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return {
    expandedTruckId,
    serviceHistoryLoading,
    handleToggleExpand,
    handleAddServiceEntry,
    handleUpdateServiceStatus,
    handleUpdateServiceDate,
    handleDeleteServiceEntry,
  };
};
